import React, { useState, useEffect } from "react";
import { ModalWrapper } from "src/shared/components/ModalWrapper";
import CommonButtons from "src/shared/components/CommonButtons";
import { Order } from "src/shared/services/interfacev2";
import { Checkbox } from "@mui/material";

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order;
  paymentData: {
    discount: string;
    ReceivedAmount: string;
    paymentMethod: string;
  };
  onPaymentDataChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onProcessPayment: () => void;
  isProcessing: boolean;
}

const Row: React.FC<{
  label: string;
  value: string;
  bold?: boolean;
}> = ({ label, value, bold }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className={`text-sm ${bold ? "text-gray-900 font-medium" : "text-gray-600"}`}>{label}</span>
    <span className={`text-sm ${bold ? "text-gray-900 font-semibold" : "text-gray-700"}`}>{value}</span>
  </div>
);

const PaymentDialog: React.FC<PaymentDialogProps> = ({ open, onClose, order, paymentData, onPaymentDataChange, onProcessPayment, isProcessing }) => {
  const [localReceivedAmount, setLocalReceivedAmount] = useState("0");
  const [error, setError] = useState("");
  const [includeOutstanding, setIncludeOutstanding] = useState(false);

  useEffect(() => {
    setLocalReceivedAmount(paymentData.ReceivedAmount || "0");
  }, [paymentData.ReceivedAmount]);

  const methodLower = (paymentData.paymentMethod || "").toLowerCase().trim();
  const isBuyOnCredit = methodLower === "buy on credit" || methodLower === "paylater" || methodLower === "pay late";

  // Get the current order (first order)
  const currentOrder = order?.orders?.[0];

  // Calculate totals from all orders if multiple exist
  const totalOrderAmount = order?.orders?.reduce((sum, o) => sum + Number(o.orderAmount || 0), 0) || 0;
  const totalDiscount = order?.orders?.reduce((sum, o) => sum + Number(o.discount || 0), 0) || 0;

  // Calculate total received from payment history (all payments)
  const totalReceivedFromHistory = order?.paymentHistory?.reduce((sum, p) => sum + Number(p.receivedAmount || 0), 0) || 0;

  // Get remaining amount from the order
  const orderRemainingAmount = Number(currentOrder?.remainingAmount || 0);
  const oldRemaining = orderRemainingAmount;

  // Get outstanding balance from order
  const customerOutstanding = Number(order?.totalOutstandingBalance || 0) - (Number(order?.orders[0]?.remainingAmount) || 0);

  // Calculate total remaining including outstanding if checkbox is checked
  const totalRemaining = oldRemaining + (includeOutstanding ? customerOutstanding : 0);

  const enteredAmount = parseFloat(localReceivedAmount || "0");

  // Validate amount against totalRemaining
  const isValidAmount = enteredAmount >= 0 && enteredAmount <= totalRemaining && !isNaN(enteredAmount);

  // Calculate new remaining based on totalRemaining
  const newRemaining = Math.max(totalRemaining - enteredAmount, 0);

  const customerName = [order?.customerDetails?.firstName, order?.customerDetails?.middleName, order?.customerDetails?.lastName].filter(Boolean).join(" ") || "N/A";

  const handleFocus = () => {
    if (localReceivedAmount === "0") setLocalReceivedAmount("");
    setError("");
  };

  const handleBlur = () => {
    if (localReceivedAmount.trim() === "") {
      setLocalReceivedAmount("0");
      const syntheticEvent = {
        target: { name: "ReceivedAmount", value: "0" },
      } as React.ChangeEvent<HTMLInputElement>;
      onPaymentDataChange(syntheticEvent);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Update local state first
    setLocalReceivedAmount(value);

    // Always update parent state
    onPaymentDataChange(e);

    // Then validate and show error
    if (value === "") {
      setError("");
      return;
    }

    const enteredVal = parseFloat(value);
    if (isNaN(enteredVal)) {
      setError("Please enter a valid number");
      return;
    }

    if (enteredVal > totalRemaining) {
      setError(`Amount cannot exceed ${totalRemaining.toFixed(2)} QAR`);
    } else {
      setError("");
    }
  };

  if (!open) return null;

  return (
    <ModalWrapper title="Payment Summary" onClose={onClose} onBack={onClose} showCloseButton showBackButton width="max-w-md">
      <div className="flex flex-col bg-white rounded-2xl max-h-[85vh]">
        <div className="px-3 py-3 overflow-y-auto scrollbar-hide">
          {/* Customer card */}
          <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-3 py-3 mb-4">
            <div className="w-9 h-9 shrink-0 rounded-full bg-white border border-gray-200 flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-500">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 20c0-4 4-6 8-6s8 2 8 6" />
              </svg>
            </div>
            <div>
              <div className="font-semibold text-gray-900 text-sm">Customer: {customerName}</div>
              <div className="text-sm text-gray-600 capitalize">Payment: {paymentData.paymentMethod}</div>
              <div className="text-xs text-gray-400">Order ID: {currentOrder?.orderId || "N/A"}</div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 text-base pb-2 border-b border-gray-200">Order Summary</h3>
            <div className="pt-1">
              {/* ✅ Outstanding Balance with checkbox - aligned like other rows */}
              <div className="flex items-center justify-between py-1.5">
                <div className="flex items-center gap-1">
                  <Checkbox
                    checked={includeOutstanding}
                    onChange={() => {
                      setIncludeOutstanding(!includeOutstanding);
                      setError("");
                    }}
                    size="small"
                  />
                  <span className="text-sm text-gray-600">Outstanding Balance</span>
                </div>
                <span className="text-sm text-gray-700">{customerOutstanding.toFixed(2)} QAR</span>
              </div>

              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-gray-600">Order Amount</span>
                <span className="text-sm text-gray-700">{totalOrderAmount.toFixed(2)} QAR</span>
              </div>

              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm text-gray-600">Discount</span>
                <span className="text-sm text-gray-700">- {totalDiscount.toFixed(2)} QAR</span>
              </div>

              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm font-medium text-gray-900">Total Received</span>
                <span className="text-sm font-semibold text-gray-900">{totalReceivedFromHistory.toFixed(2)} QAR</span>
              </div>

              <div className="flex items-center justify-between py-1.5">
                <span className="text-sm font-medium text-gray-900">Remaining Amount</span>
                <span className="text-sm font-semibold text-gray-900">{totalRemaining.toFixed(2)} QAR</span>
              </div>
            </div>
          </div>

          {/* Payment Status */}
          <div>
            <h3 className="font-semibold text-gray-900 text-base pb-2 border-b border-gray-200">Payment Status</h3>
            <div className="pt-1">
              {isBuyOnCredit ? (
                <>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">Order Remaining</span>
                    <span className="text-sm text-gray-700">{oldRemaining.toFixed(2)} QAR</span>
                  </div>
                  {includeOutstanding && customerOutstanding > 0 && (
                    <div className="flex items-center justify-between py-1.5">
                      <span className="text-sm text-gray-600">Outstanding Balance</span>
                      <span className="text-sm text-gray-700">{customerOutstanding.toFixed(2)} QAR</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm font-medium text-gray-900">Total (Incl Outstanding)</span>
                    <span className="text-sm font-semibold text-gray-900">{totalRemaining.toFixed(2)} QAR</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-gray-600">Received Amount</span>
                    <div className="flex flex-col items-end">
                      <input
                        name="ReceivedAmount"
                        type="number"
                        value={localReceivedAmount}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        min={0}
                        max={totalRemaining}
                        step="0.01"
                        placeholder="0"
                        style={{ textAlign: "right" }}
                        className={`w-28 h-9 rounded-md border px-2.5 text-right text-sm focus:outline-none ${error ? "border-red-400 focus:border-red-500" : "border-gray-300 focus:border-blue-400"}`}
                      />
                      {error && <span className="text-xs text-red-500 mt-1">{error}</span>}
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm font-medium text-gray-900">New Remaining Amount</span>
                    <span className="text-sm font-semibold text-gray-900">{newRemaining.toFixed(2)} QAR</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 flex justify-end gap-3 shrink-0">
          <CommonButtons className="h-10 w-32" label="Cancel" onClick={onClose} variant="outlineDanger" disabled={isProcessing} />
          <CommonButtons
            className="h-10 w-36"
            label={isProcessing ? "Processing..." : "Complete"}
            onClick={onProcessPayment}
            variant="primary"
            disabled={isProcessing || (!isBuyOnCredit && (!isValidAmount || localReceivedAmount.trim() === "" || parseFloat(localReceivedAmount) <= 0))}
          />
        </div>
      </div>
    </ModalWrapper>
  );
};

export default PaymentDialog;
